// src/components/Constructor.js
import { useAppContext } from "../../context/AppContext";
import {
  ButtonsTrans,
  ConstructorTrans,
  TagListTrans,
  WorkoutTrans,
} from "../../utils/translations";
import bgImg from "../../assets/images/bgImg-4.png";
import DropdownButton from "../utilPages/DropdownButton";
import { useState, useMemo, useEffect } from "react";
import TagList from "../utilPages/TagList";
import { assemblePractice, assemblePracticeTrial } from "../../services/practiceService";
import YouTubeVideoDisplay from "../utilPages/YouTubeVideoDisplay";
import { motion, AnimatePresence } from "framer-motion";
import MarkAsCompleteButton from "../utilPages/MarkAsCompleteButton";

/** Omitted from the tag pool when drawing 3–6 tags for a fully empty (Random) build. */
const TAG_IDS_EXCLUDED_FROM_RANDOM_TAG_POOL = new Set([
  "noInversions",
  "laying",
  "seated",
]);

function pickRandomTagSubset(tagIds, minCount, maxCount) {
  const pool = tagIds.filter((id) => !TAG_IDS_EXCLUDED_FROM_RANDOM_TAG_POOL.has(id));
  if (!pool.length) return [];
  const desired =
    Math.floor(Math.random() * (maxCount - minCount + 1)) + minCount;
  const want = Math.min(desired, pool.length);
  const copy = [...pool];
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy.slice(0, want);
}

export default function Constructor({ isTrial = false }) {
  const { language, user, selectionStates, setSelectionStates, toggleComplete, premiumRoles, token } = useAppContext();

  const [selectedTags, setSelectedTags] = useState([]);
  const [constructedPracticeLink, setConstructedPracticeLink] = useState(null);
  const [practiceUuid, setPracticeUuid] = useState("");
  const [filteredVideos, setFilteredVideos] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isFilterEmpty, setIsFilterEmpty] = useState(true);
  const [isBuilt, setIsBuilt] = useState(false);
  const [useAlternativePlayer, setUseAlternativePlayer] = useState(false);
  const [hasMarkedComplete, setHasMarkedComplete] = useState(false);

  const tagList = useMemo(
    () => [
      { id: "balance", label: TagListTrans[language].balance },
      { id: "lotus", label: TagListTrans[language].lotus },
      { id: "hipOpenners", label: TagListTrans[language].hipOpeners },
      { id: "shavasana", label: TagListTrans[language].guidedShavasana },
      { id: "breathing", label: TagListTrans[language].breathing },
      { id: "legStrength", label: TagListTrans[language].legStrength },
      { id: "armStrength", label: TagListTrans[language].armStrength },
      { id: "flexibility", label: TagListTrans[language].flexibility },
      { id: "backStrength", label: TagListTrans[language].backStrength },
      { id: "handstands", label: TagListTrans[language].handstands },
      { id: "core", label: TagListTrans[language].core },
      { id: "armBalances", label: TagListTrans[language].armBalances },
      { id: "backBends", label: TagListTrans[language].backBends },
      { id: "splits", label: TagListTrans[language].splits },
      { id: "twists", label: TagListTrans[language].twists },
      { id: "surya", label: TagListTrans[language].surya },
      { id: "shoulders", label: TagListTrans[language].shoulders },
      { id: "chestOpeners", label: TagListTrans[language].chestOpeners },
      { id: "seated", label: TagListTrans[language].seated },
      { id: "laying", label: TagListTrans[language].laying },
      { id: "noInversions", label: TagListTrans[language].noInversions },
      { id: "glutes", label: TagListTrans[language].glutes },
    ],
    [language]
  );

  const levelOptions = useMemo(
    () => [
      { key: "", name: WorkoutTrans[language].any },
      { key: "beginner", name: WorkoutTrans[language].beginner },
      { key: "experienced", name: WorkoutTrans[language].experienced },
      { key: "advanced", name: WorkoutTrans[language].advanced },
    ],
    [language]
  );
  const typeOptions = useMemo(
    () => [
      { key: "", name: WorkoutTrans[language].any },
      { key: "recovery", name: WorkoutTrans[language].recovery },
      { key: "progression", name: WorkoutTrans[language].progression },
    ],
    [language]
  );
  const durationOptions = useMemo(
    () => [
      { key: null, name: WorkoutTrans[language].anyFem },
      { key: 15, name: WorkoutTrans[language].fifteen },
      { key: 30, name: WorkoutTrans[language].thirty },
      { key: 60, name: WorkoutTrans[language].sixty },
      { key: 90, name: WorkoutTrans[language].ninety },
    ],
    [language]
  );

  const [selectedLevel, setSelectedLevel] = useState(levelOptions[0]);
  const [selectedType, setSelectedType] = useState(typeOptions[0]);
  const [selectedDuration, setSelectedDuration] = useState(
    durationOptions[0]
  );

  const getPrimaryUrl = (piece) =>
    piece?.videos?.[language]?.default ||
    piece?.videos?.ru?.default ||
    piece?.videos?.en?.default ||
    piece?.videoURL ||
    "";

  const getAlternativeUrl = (piece) =>
    piece?.videos?.[language]?.alternative ||
    piece?.videos?.ru?.alternative ||
    piece?.videos?.en?.alternative ||
    piece?.alternativeVideoURL ||
    "";
  const getActiveVideoUrl = (piece) =>
    useAlternativePlayer ? (getAlternativeUrl(piece) || getPrimaryUrl(piece)) : getPrimaryUrl(piece);

  const handleSearch = async () => {
    try {
      const searchSnapshot = {
        level: selectedLevel.key,
        type: selectedType.key,
        duration: selectedDuration.key,
        tags: selectedTags,
      };
      const isFullRandom = Object.values(searchSnapshot).every(
        (value) =>
          value === "" ||
          value === null ||
          value === undefined ||
          (Array.isArray(value) && value.length === 0)
      );

      const tagsForRequest = isFullRandom
        ? pickRandomTagSubset(
            tagList.map((t) => t.id),
            3,
            6
          )
        : selectedTags;

      const params = {
        level: selectedLevel.key,
        type: selectedType.key,
        duration: selectedDuration.key,
        tags: tagsForRequest,
      };
      const response = isTrial
        ? await assemblePracticeTrial(params)
        : await assemblePractice(params, token);
      if (isFullRandom) {
        setSelectedTags(tagsForRequest);
      } else if (!selectedTags.length) {
        const allTags = Array.from(new Set(response.flatMap((p) => p.tags || [])));
        setSelectedTags(allTags);
      }

      // 2) Level: наиболее частый уровень среди кусков
      if (!selectedLevel) {
        const levelCount = {};
        response.forEach(p => p.level.forEach(l => {
          levelCount[l] = (levelCount[l] || 0) + 1;
        }));
        const mostLevel = Object.entries(levelCount)
          .sort((a, b) => b[1] - a[1])[0]?.[0] || "";
        setSelectedLevel(
          levelOptions.find(o => o.key === mostLevel) || levelOptions[0]
        );
      }

      // 3) Type: наиболее частый тип
      if (!selectedType) {
        const typeCount = {};
        response.forEach(p => p.type.forEach(t => {
          typeCount[t] = (typeCount[t] || 0) + 1;
        }));
        const mostType = Object.entries(typeCount)
          .sort((a, b) => b[1] - a[1])[0]?.[0] || "";
        setSelectedType(
          typeOptions.find(o => o.key === mostType) || typeOptions[0]
        );
      }

      // 4) Duration: полная длительность всех кусков
      const totalTime = response.reduce((sum, p) => sum + p.duration, 0);
      setSelectedDuration({ key: totalTime, name: `${totalTime} мин` });
      setFilteredVideos(response);
      setCurrentIndex(0);
      setConstructedPracticeLink(
        response.length > 0 ? getActiveVideoUrl(response[0]) : null
      );
      setIsBuilt(true);
      setHasMarkedComplete(false);
      setPracticeUuid(response
        .map(piece => piece.uuid)
        .join(","));
    } catch (error) {
      console.error("Error fetching videos:", error);
    }
  };

  const handleVideoEnd = () => {
    setCurrentIndex((i) => {
      const next = i + 1;
      if (next < filteredVideos.length) {
        setConstructedPracticeLink(getActiveVideoUrl(filteredVideos[next]));
        return next;
      }
      return i;
    });
  };

  const handlePrev = () => {
    setCurrentIndex((i) => {
      const next = i - 1;
      if (next < filteredVideos.length) {
        setConstructedPracticeLink(getActiveVideoUrl(filteredVideos[next]));
        return next;
      }
      return i;
    });
  };

  const handleReset = () => {
    setSelectedTags([]);
    setSelectedLevel(levelOptions[0]);
    setSelectedType(typeOptions[0]);
    setSelectedDuration(durationOptions[0]);
    setFilteredVideos([]);
    setCurrentIndex(0);
    setIsBuilt(false);
    setConstructedPracticeLink(null);
    setUseAlternativePlayer(false);
    setHasMarkedComplete(false);
  };

  useEffect(() => {
    if (!filteredVideos.length) {
      setConstructedPracticeLink(null);
      return;
    }
    const currentPiece = filteredVideos[currentIndex];
    setConstructedPracticeLink(currentPiece ? getActiveVideoUrl(currentPiece) : null);
  }, [useAlternativePlayer, filteredVideos, currentIndex]);

  useEffect(() => {
    if (!isTrial && user && !premiumRoles.includes(user?.role)) {
      window.location.assign("/not-found");
    }
    const searchObject = {
      level: selectedLevel.key,
      type: selectedType.key,
      duration: selectedDuration.key,
      tags: selectedTags,
    };
    const allEmpty = Object.values(searchObject).every(
      (value) =>
        value === "" ||
        value === null ||
        value === undefined ||
        (Array.isArray(value) && value.length === 0)
    );
    setIsFilterEmpty(allEmpty);
    if (user && !isTrial) {
      setSelectionStates({
        isComplete: user?.completedPractices?.includes(practiceUuid) || false,
        isSaved: user?.savedPractices?.includes(practiceUuid) || false,
      });
    }
  }, [user, isTrial, selectedLevel, selectedType, selectedDuration, selectedTags]);

  const hasAlternativeVideos = filteredVideos.some((piece) => Boolean(getAlternativeUrl(piece)));

  return (
    <main className="flex min-h-screen flex-col justify-between mt-5 mb-10 border-t-[1.5px] lg:border-black">
      <div className="flex-1 grid md:grid-cols-3 md:w-10/12 w-11/12 pt-10 mx-auto gap-10">
        <div className="col-span-2 flex flex-col">
          <h2 className="md:text-4xl mb-2 md:mb-6 text-2xl md:text-left text-center">{isBuilt ? ConstructorTrans[language].workoutReady : (isTrial ? (ConstructorTrans[language].trialHeader ?? ConstructorTrans[language].header) : ConstructorTrans[language].header)}</h2>
          {isTrial && !isBuilt && (
            <p className="text-sm text-ink-mid mb-4 md:text-left text-center">
              {ConstructorTrans[language].trialFullVersionBefore1}
              <a href="/onlineClub" className="text-clay hover:underline font-medium">{ConstructorTrans[language].trialLinkOnlineClub}</a>
              {ConstructorTrans[language].trialFullVersionBetween}
              <a href="/schedule" className="text-clay hover:underline font-medium">{ConstructorTrans[language].trialLinkSchedule}</a>.
            </p>
          )}
          <AnimatePresence>
            {!isBuilt && (
              <motion.div
                key="selectors"
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.4 }}
              >
                <div className="flex flex-wrap justify-start gap-4 md:gap-8 mt-5 md:text-base text-sm">
                  <DropdownButton
                    title={WorkoutTrans[language].level}
                    options={levelOptions}
                    selectedOption={selectedLevel}
                    setSelectedOption={setSelectedLevel}
                  />
                  <DropdownButton
                    title={WorkoutTrans[language].type}
                    options={typeOptions}
                    selectedOption={selectedType}
                    setSelectedOption={setSelectedType}
                  />
                  <DropdownButton
                    title={WorkoutTrans[language].duration}
                    options={durationOptions}
                    selectedOption={selectedDuration}
                    setSelectedOption={setSelectedDuration}
                  />
                </div>

                <TagList
                  tags={tagList}
                  selectedTags={selectedTags}
                  setSelectedTags={setSelectedTags}
                />
              </motion.div>
            )}

            {isBuilt && (
              <motion.div
                key="summary"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.4 }}
                className="bg-white/70 p-4 rounded-md shadow-lg text-sm"
              >
                <p className="text-xs mb-3">{ConstructorTrans[language].description}</p>
                <p>
                  <strong>{WorkoutTrans[language].level}:</strong>{" "}
                  {selectedLevel.name}
                </p>
                <p>
                  <strong>{WorkoutTrans[language].type}:</strong>{" "}
                  {selectedType.name}
                </p>
                <p>
                  <strong>{WorkoutTrans[language].duration}:</strong>{" "}
                  {selectedDuration.name}
                </p>
                {selectedTags.length ? <p>
                  <strong>{ConstructorTrans[language].tags}:</strong>{" "}
                  {selectedTags
                    .map(id => tagList.find(t => t.id === id)?.label)
                    .join(", ")}
                </p> : ""}

              </motion.div>
            )}
          </AnimatePresence>

          <div className="justify-start text-center mt-5">
            <button
              onClick={isBuilt ? handleReset : handleSearch}
              className="bg-pink rounded-3xl text-white text-md py-2 px-7 hover:bg-brown w-fit mt-1 mb-0"
            >
              {isBuilt ? ButtonsTrans[language].buildAgain :
                isFilterEmpty
                  ? ButtonsTrans[language].random
                  : ButtonsTrans[language].search
              }
            </button>
          </div>

          <div className="w-full mx-auto mb-8 mt-5">
            {constructedPracticeLink && (
              <div>
                <div className="flex justify-between mt-0 mb-3">
                  <button
                    onClick={handlePrev}
                    disabled={currentIndex === 0}
                    className="bg-beige hover:bg-pink text-white py-0 px-4 rounded disabled:opacity-50 disabled:hover:bg-beige"
                  >
                    {ConstructorTrans[language].previous}
                  </button>
                  <button
                    onClick={handleVideoEnd}
                    disabled={currentIndex >= filteredVideos.length - 1}
                    className="bg-beige hover:bg-pink text-white px-4 py-0 rounded disabled:opacity-50 disabled:hover:bg-beige"
                  >
                    {ConstructorTrans[language].next}
                  </button>
                </div>
                <div className="w-full aspect-video">
                  <YouTubeVideoDisplay
                    videoUrl={constructedPracticeLink}
                    videoUrls={filteredVideos.map((v) => getActiveVideoUrl(v))}
                    currentIndex={currentIndex}
                    onEnded={handleVideoEnd}
                  />
                </div>
                {(/* hasAlternativeVideos || */ (!isTrial && user)) && (
                  <div className="mt-3 flex items-center justify-between gap-3">
                    <div>
                      {/* {hasAlternativeVideos && (
                        <div className="w-fit h-7 rounded-full border border-stone-300 bg-stone-100 p-0.5 flex gap-1 items-center">
                          <button
                            onClick={() => setUseAlternativePlayer(false)}
                            className={`rounded-full h-6 px-3 text-xs leading-none flex items-center transition-colors ${
                              !useAlternativePlayer
                                ? "bg-white text-stone-900 shadow-sm"
                                : "text-stone-600 hover:text-stone-900"
                            }`}
                          >
                            {`${ConstructorTrans[language].player} 1`}
                          </button>
                          <button
                            onClick={() => setUseAlternativePlayer(true)}
                            className={`rounded-full h-6 px-3 text-xs leading-none flex items-center transition-colors ${
                              useAlternativePlayer
                                ? "bg-white text-stone-900 shadow-sm"
                                : "text-stone-600 hover:text-stone-900"
                            }`}
                          >
                            {`${ConstructorTrans[language].player} 2`}
                          </button>
                        </div>
                      )} */}
                    </div>
                    {!isTrial && user && (
                      <div className="w-fit ml-auto">
                        <MarkAsCompleteButton
                          isComplete={hasMarkedComplete || selectionStates.isComplete}
                          toggleComplete={() => {
                            if (hasMarkedComplete || selectionStates.isComplete) return;
                            setHasMarkedComplete(true);
                            toggleComplete(practiceUuid);
                          }}
                          variant="minimal"
                          className="!min-h-0 !h-7 !px-3 !py-0 !text-xs leading-none"
                          labelClassName="text-xs leading-none"
                          activeIconClassName="h-3 w-3 mr-1.5"
                          inactiveIconClassName="h-3 w-3 mr-1.5"
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>
              
            )}
          </div>
        </div>

        <div className="col-span-1 pt-4 hidden md:block">
          <img
            src={bgImg}
            alt="Description"
            className="w-full h-full object-cover"
          />
        </div>
      </div>
    </main>
  );
}
